"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Image from "next/image";

export default function Hero() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (window.innerWidth / 2 - e.clientX) / 25;
      const y = (window.innerHeight / 2 - e.clientY) / 25;
      setMousePosition({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative flex items-center bg-blue-dark overflow-hidden pt-24 pb-0 lg:min-h-screen lg:pt-20 lg:pb-12">
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/formacao-devs-ia/Hero-Banner-1.jpg"
          alt="Background"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-blue-dark/40" />
      </div>

      <div className="absolute bottom-0 right-0 w-full md:w-2/3 h-full z-10 pointer-events-none flex justify-end items-end opacity-80">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/formacao-devs-ia/Pixels.png"
          alt=""
          className="object-contain max-h-full"
        />
      </div>

      <div className="hidden lg:block absolute inset-0 z-10 pointer-events-none overflow-hidden font-mono text-sm md:text-base">
        <motion.div
          animate={{ y: [0, -20, 0], opacity: [0.2, 0.6, 0.2] }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          className="absolute top-[20%] left-[5%] text-blue-main"
        >
          &lt;FormacaoIA /&gt;
        </motion.div>
        <motion.div
          animate={{ y: [0, -15, 0], opacity: [0.3, 0.8, 0.3] }}
          transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 2 }}
          className="absolute top-[30%] right-[10%] text-blue-main text-xl"
        >
          import &#123; AI &#125; from &apos;agibank&apos;;
        </motion.div>
      </div>

      <div className="container-custom relative z-20 w-full mt-4 lg:mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-center">
          {/* Desktop */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hidden lg:flex col-span-5 flex-col items-start justify-center text-left w-full z-30"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/formacao-devs-ia/Logo_Formacao_Devs.png"
              alt="Formação de Devs nativos em IA"
              className="w-full max-w-[700px] xl:max-w-[850px] mb-8 object-contain"
              style={{ objectPosition: "0% 50%" }}
            />
            <div className="w-full flex flex-col items-start pl-0 lg:pl-[32px] xl:pl-[32px]">
              <a
                href="https://job-boards.greenhouse.io/agibank/jobs/5270544008"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-secondary-main text-blue-dark px-10 py-4 rounded-full font-bold text-lg hover:bg-blue-main hover:text-white transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(119,223,64,0.3)]"
              >
                Fazer inscrição
              </a>
              <motion.div
                animate={{ x: mousePosition.x * 0.3, y: mousePosition.y * 0.3 }}
                transition={{ type: "spring", stiffness: 50, damping: 20 }}
                className="mt-10 text-base text-blue-main font-mono opacity-90 leading-relaxed text-left"
              >
                <p>&lt;DevAgibank&gt; &lt;Carreira</p>
                <p className="pl-4">status=&quot;growing&quot; /&gt; &lt;Futuro</p>
                <p className="pl-4">bright=&#123;true&#125; /&gt; &lt;/DevAgibank&gt; //</p>
                <p className="mt-2 text-secondary-main">Props: você</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex lg:hidden col-span-1 flex-col items-center justify-center text-center w-full z-30"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/formacao-devs-ia/Logo_Formacao_Devs.png"
              alt="Formação de Devs nativos em IA"
              className="w-[90%] max-w-[400px] mb-8 object-contain"
            />
            <div className="w-full flex flex-col items-center">
              <a
                href="https://job-boards.greenhouse.io/agibank/jobs/5270544008"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-secondary-main text-blue-dark px-10 py-4 rounded-full font-bold text-lg hover:bg-blue-main hover:text-white transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(119,223,64,0.3)]"
              >
                Fazer inscrição
              </a>
              <div className="mt-8 text-xs sm:text-sm text-blue-main font-mono opacity-90 leading-relaxed text-center">
                <p>&lt;DevAgibank&gt; &lt;Carreira</p>
                <p>status=&quot;growing&quot; /&gt; &lt;Futuro</p>
                <p>bright=&#123;true&#125; /&gt; &lt;/DevAgibank&gt; //</p>
                <p className="mt-2 text-secondary-main">Props: você</p>
              </div>
            </div>
          </motion.div>

          {/* Imagem do Dev */}
          <div className="col-span-1 lg:col-span-7 relative h-[450px] md:h-[600px] lg:h-[800px] xl:h-[950px] flex justify-center items-end pointer-events-none -mt-10 lg:mt-0 z-20 overflow-hidden">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] lg:w-[500px] h-[300px] lg:h-[500px] bg-blue-main rounded-full blur-[80px] lg:blur-[120px] opacity-30 z-0" />
            <motion.div
              animate={{ y: [0, 25, 0], opacity: [0.2, 0.6, 0.2] }}
              transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 0.5 }}
              className="lg:hidden absolute top-[35%] right-[2%] text-white font-mono text-base z-0"
            >
              const future = new Developer();
            </motion.div>
            <motion.div
              animate={{ x: mousePosition.x, y: mousePosition.y }}
              transition={{ type: "spring", stiffness: 50, damping: 20 }}
              className="relative w-full h-full flex justify-center items-end z-10 origin-bottom"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/formacao-devs-ia/Dev-01.png"
                alt="Desenvolvedor Agibank"
                className="object-contain max-h-full drop-shadow-2xl"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, 30, 0], opacity: [0.3, 0.8, 0.3] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
              className="lg:hidden absolute bottom-[35%] left-[5%] text-secondary-main font-mono text-base z-20"
            >
              if (learning) &#123; grow(); &#125;
            </motion.div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block absolute inset-0 z-30 pointer-events-none overflow-hidden font-mono text-sm md:text-base">
        <motion.div
          animate={{ y: [0, 30, 0], opacity: [0.3, 0.8, 0.3] }}
          transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-[20%] left-[45%] lg:left-[55%] text-secondary-main"
        >
          if (learning) &#123; grow(); &#125;
        </motion.div>
        <motion.div
          animate={{ y: [0, 25, 0], opacity: [0.2, 0.6, 0.2] }}
          transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 0.5 }}
          className="absolute top-[65%] right-[15%] text-white"
        >
          const future = new Developer();
        </motion.div>
      </div>

      <div className="lg:hidden absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-blue-dark via-blue-dark/80 to-transparent z-40 pointer-events-none" />
    </section>
  );
}
